/**
 * X-Frame Companion App - Exercise Library Seed Data
 * 
 * Comprehensive exercise database organized by muscle group with
 * movement bias tags for X-Frame program compatibility.
 * 
 * Equipment available: Squat rack, Smith machine, Dual pulley system,
 * Pull-up bar, Landmine, Barbell, EZ curl bar, Dumbbells (5-80lb),
 * Dip bars, Adjustable bench, Leg press, Hack squat, Leg curl, Leg extension
 */

// ============================================
// TYPES & INTERFACES
// ============================================

export type MuscleGroup =
  | 'chest'
  | 'lats'
  | 'upper_back'
  | 'rear_delt'
  | 'side_delt'
  | 'front_delt'
  | 'biceps'
  | 'triceps'
  | 'quads'
  | 'hamstrings'
  | 'glutes'
  | 'calves'
  | 'abs';

export type MovementBias =
  // Back biases
  | 'lat_bias_pulldown'
  | 'lat_bias_row'
  | 'upper_back_bias_pulldown'
  | 'upper_back_bias_row'
  | 'lat_isolation'
  // Chest biases
  | 'mid_chest_press'
  | 'upper_chest_press'
  | 'lower_chest_press'
  | 'chest_fly'
  // Shoulder biases
  | 'side_delt_isolation'
  | 'rear_delt_isolation'
  | 'front_delt_press'
  | 'front_delt_isolation'
  // Arm biases
  | 'bicep_long_head'
  | 'bicep_short_head'
  | 'bicep_brachialis'
  | 'tricep_long_head'
  | 'tricep_lateral_head'
  | 'tricep_medial_head'
  // Leg biases
  | 'quad_compound'
  | 'quad_isolation'
  | 'hamstring_curl'
  | 'hamstring_hinge'
  | 'glute_isolation'
  | 'glute_compound'
  | 'calf_gastroc'
  | 'calf_soleus'
  // Core
  | 'abs_flexion'
  | 'abs_rotation'
  | 'abs_isometric'
  | 'abs_vacuum';

export type Equipment =
  | 'barbell'
  | 'dumbbell'
  | 'cable'
  | 'smith_machine'
  | 'machine'
  | 'bodyweight'
  | 'ez_bar'
  | 'landmine'
  | 'pull_up_bar'
  | 'dip_bars'
  | 'bench';

export type CableAttachment =
  | 'rope'
  | 'v_bar'
  | 'wide_bar'
  | 'ez_curl_bar'
  | 'straight_bar'
  | 'd_handle'
  | 'mag_grip_neutral'
  | 'rot8_handles';

export interface Exercise {
  id: string;
  name: string;
  muscleGroup: MuscleGroup;
  secondaryMuscles?: MuscleGroup[];
  movementBias: MovementBias;
  equipment: Equipment[];
  cableAttachment?: CableAttachment[];
  isCompound: boolean;
  isUnilateral: boolean;
  benchAngle?: 'flat' | 'incline' | 'decline' | 'upright' | 'prone';
  cablePosition?: 'high' | 'mid' | 'low' | 'floor';
  stance?: 'narrow' | 'wide' | 'normal' | 'split' | 'single_leg';
  grip?: 'overhand' | 'underhand' | 'neutral' | 'mixed' | 'wide' | 'close';
  notes?: string;
}

// ============================================
// CHEST EXERCISES
// ============================================

export const CHEST_EXERCISES: Exercise[] = [
  // Barbell Pressing
  {
    id: 'chest_bb_bench_flat',
    name: 'Barbell Bench Press',
    muscleGroup: 'chest',
    secondaryMuscles: ['front_delt', 'triceps'],
    movementBias: 'mid_chest_press',
    equipment: ['barbell', 'bench'],
    isCompound: true,
    isUnilateral: false,
    benchAngle: 'flat'
  },
  {
    id: 'chest_bb_bench_incline',
    name: 'Barbell Incline Bench Press',
    muscleGroup: 'chest',
    secondaryMuscles: ['front_delt', 'triceps'],
    movementBias: 'upper_chest_press',
    equipment: ['barbell', 'bench'],
    isCompound: true,
    isUnilateral: false,
    benchAngle: 'incline'
  },
  
  // Dumbbell Pressing
  {
    id: 'chest_db_bench_flat',
    name: 'Dumbbell Bench Press',
    muscleGroup: 'chest',
    secondaryMuscles: ['front_delt', 'triceps'],
    movementBias: 'mid_chest_press',
    equipment: ['dumbbell', 'bench'],
    isCompound: true,
    isUnilateral: false,
    benchAngle: 'flat'
  },
  {
    id: 'chest_db_bench_incline',
    name: 'Dumbbell Incline Bench Press',
    muscleGroup: 'chest',
    secondaryMuscles: ['front_delt', 'triceps'],
    movementBias: 'upper_chest_press',
    equipment: ['dumbbell', 'bench'],
    isCompound: true,
    isUnilateral: false,
    benchAngle: 'incline'
  },
  
  // Smith Machine Pressing
  {
    id: 'chest_smith_bench_flat',
    name: 'Smith Machine Bench Press',
    muscleGroup: 'chest',
    secondaryMuscles: ['front_delt', 'triceps'],
    movementBias: 'mid_chest_press',
    equipment: ['smith_machine', 'bench'],
    isCompound: true,
    isUnilateral: false,
    benchAngle: 'flat'
  },
  {
    id: 'chest_smith_bench_incline',
    name: 'Smith Machine Incline Bench Press',
    muscleGroup: 'chest',
    secondaryMuscles: ['front_delt', 'triceps'],
    movementBias: 'upper_chest_press',
    equipment: ['smith_machine', 'bench'],
    isCompound: true,
    isUnilateral: false,
    benchAngle: 'incline'
  },
  
  // Flyes
  {
    id: 'chest_cable_fly_high_to_low',
    name: 'Cable Fly (High to Low)',
    muscleGroup: 'chest',
    movementBias: 'lower_chest_press',
    equipment: ['cable'],
    cableAttachment: ['d_handle'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'high'
  },
  {
    id: 'chest_cable_fly_low_to_high',
    name: 'Cable Fly (Low to High)',
    muscleGroup: 'chest',
    movementBias: 'upper_chest_press',
    equipment: ['cable'],
    cableAttachment: ['d_handle'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'low'
  },
  {
    id: 'chest_cable_fly_seated',
    name: 'Seated Cable Fly',
    muscleGroup: 'chest',
    movementBias: 'chest_fly',
    equipment: ['cable', 'bench'],
    cableAttachment: ['d_handle'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'mid'
  },
  {
    id: 'chest_cable_fly_incline',
    name: 'Incline Cable Fly',
    muscleGroup: 'chest',
    movementBias: 'upper_chest_press',
    equipment: ['cable', 'bench'],
    cableAttachment: ['d_handle'],
    isCompound: false,
    isUnilateral: false,
    benchAngle: 'incline',
    cablePosition: 'low'
  },
  {
    id: 'chest_cable_fly_crossover',
    name: 'Cable Crossover Fly',
    muscleGroup: 'chest',
    movementBias: 'chest_fly',
    equipment: ['cable'],
    cableAttachment: ['d_handle'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'mid'
  },
  {
    id: 'chest_landmine_fly',
    name: 'Landmine Fly',
    muscleGroup: 'chest',
    movementBias: 'chest_fly',
    equipment: ['landmine'],
    isCompound: false,
    isUnilateral: true
  },
  
  // Dips & Push-ups
  {
    id: 'chest_dips_bodyweight',
    name: 'Dips',
    muscleGroup: 'chest',
    secondaryMuscles: ['triceps', 'front_delt'],
    movementBias: 'lower_chest_press',
    equipment: ['dip_bars', 'bodyweight'],
    isCompound: true,
    isUnilateral: false,
    notes: 'Lean forward for chest emphasis'
  },
  {
    id: 'chest_dips_weighted',
    name: 'Weighted Dips',
    muscleGroup: 'chest',
    secondaryMuscles: ['triceps', 'front_delt'],
    movementBias: 'lower_chest_press',
    equipment: ['dip_bars'],
    isCompound: true,
    isUnilateral: false,
    notes: 'Lean forward for chest emphasis'
  },
  {
    id: 'chest_pushups',
    name: 'Push-ups',
    muscleGroup: 'chest',
    secondaryMuscles: ['triceps', 'front_delt'],
    movementBias: 'mid_chest_press',
    equipment: ['bodyweight'],
    isCompound: true,
    isUnilateral: false
  },
  
  // Landmine
  {
    id: 'chest_landmine_press_low',
    name: 'Low Angle Landmine Press',
    muscleGroup: 'chest',
    secondaryMuscles: ['front_delt', 'triceps'],
    movementBias: 'upper_chest_press',
    equipment: ['landmine'],
    isCompound: true,
    isUnilateral: false,
    notes: 'Keep angle low for chest emphasis vs shoulder'
  }
];

// ============================================
// BACK EXERCISES - LATS
// ============================================

export const LAT_EXERCISES: Exercise[] = [
  // Pulldowns - Lat Bias
  {
    id: 'lats_pulldown_wide',
    name: 'Wide Grip Lat Pulldown',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps', 'rear_delt'],
    movementBias: 'lat_bias_pulldown',
    equipment: ['cable'],
    cableAttachment: ['wide_bar'],
    isCompound: true,
    isUnilateral: false,
    cablePosition: 'high',
    grip: 'overhand'
  },
  {
    id: 'lats_pulldown_close_neutral',
    name: 'Close Grip Neutral Pulldown',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps'],
    movementBias: 'lat_bias_pulldown',
    equipment: ['cable'],
    cableAttachment: ['v_bar', 'mag_grip_neutral'],
    isCompound: true,
    isUnilateral: false,
    cablePosition: 'high',
    grip: 'neutral'
  },
  {
    id: 'lats_pulldown_underhand',
    name: 'Underhand Lat Pulldown',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps'],
    movementBias: 'lat_bias_pulldown',
    equipment: ['cable'],
    cableAttachment: ['straight_bar', 'ez_curl_bar'],
    isCompound: true,
    isUnilateral: false,
    cablePosition: 'high',
    grip: 'underhand'
  },
  {
    id: 'lats_pulldown_single_arm',
    name: 'Single Arm Lat Pulldown',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps'],
    movementBias: 'lat_bias_pulldown',
    equipment: ['cable'],
    cableAttachment: ['d_handle', 'rot8_handles'],
    isCompound: true,
    isUnilateral: true,
    cablePosition: 'high'
  },
  {
    id: 'lats_straight_arm_pushdown',
    name: 'Straight Arm Lat Pushdown',
    muscleGroup: 'lats',
    movementBias: 'lat_isolation',
    equipment: ['cable'],
    cableAttachment: ['straight_bar', 'rope', 'wide_bar'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'high',
    notes: 'Lat isolation - keep arms straight'
  },
  
  // Rows - Lat Bias
  {
    id: 'lats_cable_row_low',
    name: 'Low Cable Row',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    movementBias: 'lat_bias_row',
    equipment: ['cable'],
    cableAttachment: ['v_bar', 'mag_grip_neutral', 'd_handle'],
    isCompound: true,
    isUnilateral: false,
    cablePosition: 'low'
  },
  {
    id: 'lats_cable_row_single_arm',
    name: 'Single Arm Cable Row',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    movementBias: 'lat_bias_row',
    equipment: ['cable'],
    cableAttachment: ['d_handle', 'rot8_handles'],
    isCompound: true,
    isUnilateral: true,
    cablePosition: 'low'
  },
  {
    id: 'lats_landmine_row_single',
    name: 'Single Arm Landmine Row',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    movementBias: 'lat_bias_row',
    equipment: ['landmine'],
    isCompound: true,
    isUnilateral: true,
    notes: 'Meadows row variation'
  },
  {
    id: 'lats_meadows_row',
    name: 'Meadows Row',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    movementBias: 'lat_bias_row',
    equipment: ['landmine'],
    isCompound: true,
    isUnilateral: true,
    notes: 'Perpendicular stance to bar'
  },
  {
    id: 'lats_db_row',
    name: 'Dumbbell Row',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    movementBias: 'lat_bias_row',
    equipment: ['dumbbell', 'bench'],
    isCompound: true,
    isUnilateral: true
  },
  {
    id: 'lats_seal_row',
    name: 'Seal Row',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    movementBias: 'lat_bias_row',
    equipment: ['dumbbell', 'barbell', 'bench'],
    isCompound: true,
    isUnilateral: false,
    benchAngle: 'flat',
    notes: 'Chest supported on elevated bench'
  },
  
  // Pull-ups - Lat Bias
  {
    id: 'lats_pullup_wide',
    name: 'Wide Grip Pull-up',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    movementBias: 'lat_bias_pulldown',
    equipment: ['pull_up_bar', 'bodyweight'],
    isCompound: true,
    isUnilateral: false,
    grip: 'wide'
  },
  {
    id: 'lats_pullup_normal',
    name: 'Pull-up',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps', 'upper_back'],
    movementBias: 'lat_bias_pulldown',
    equipment: ['pull_up_bar', 'bodyweight'],
    isCompound: true,
    isUnilateral: false,
    grip: 'overhand'
  },
  {
    id: 'lats_chinup',
    name: 'Chin-up',
    muscleGroup: 'lats',
    secondaryMuscles: ['biceps'],
    movementBias: 'lat_bias_pulldown',
    equipment: ['pull_up_bar', 'bodyweight'],
    isCompound: true,
    isUnilateral: false,
    grip: 'underhand'
  }
];

// ============================================
// BACK EXERCISES - UPPER BACK
// ============================================

export const UPPER_BACK_EXERCISES: Exercise[] = [
  // Pulldowns - Upper Back Bias
  {
    id: 'upper_back_pulldown_wide_overhand',
    name: 'Wide Overhand Pulldown',
    muscleGroup: 'upper_back',
    secondaryMuscles: ['lats', 'rear_delt', 'biceps'],
    movementBias: 'upper_back_bias_pulldown',
    equipment: ['cable'],
    cableAttachment: ['wide_bar'],
    isCompound: true,
    isUnilateral: false,
    cablePosition: 'high',
    grip: 'wide',
    notes: 'Pull to upper chest, squeeze scapula'
  },
  
  // Rows - Upper Back Bias
  {
    id: 'upper_back_bb_row',
    name: 'Barbell Row',
    muscleGroup: 'upper_back',
    secondaryMuscles: ['lats', 'biceps', 'rear_delt'],
    movementBias: 'upper_back_bias_row',
    equipment: ['barbell'],
    isCompound: true,
    isUnilateral: false,
    grip: 'overhand',
    notes: 'Row to lower chest for upper back emphasis'
  },
  {
    id: 'upper_back_cable_row_high',
    name: 'High Cable Row',
    muscleGroup: 'upper_back',
    secondaryMuscles: ['rear_delt', 'biceps'],
    movementBias: 'upper_back_bias_row',
    equipment: ['cable'],
    cableAttachment: ['rope', 'wide_bar', 'd_handle'],
    isCompound: true,
    isUnilateral: false,
    cablePosition: 'high',
    notes: 'Row to face/upper chest'
  },
  {
    id: 'upper_back_cable_row_wide',
    name: 'Wide Grip Cable Row',
    muscleGroup: 'upper_back',
    secondaryMuscles: ['rear_delt', 'lats', 'biceps'],
    movementBias: 'upper_back_bias_row',
    equipment: ['cable'],
    cableAttachment: ['wide_bar'],
    isCompound: true,
    isUnilateral: false,
    cablePosition: 'low',
    grip: 'wide'
  },
  {
    id: 'upper_back_cable_row_rope',
    name: 'Rope Cable Row',
    muscleGroup: 'upper_back',
    secondaryMuscles: ['rear_delt', 'biceps'],
    movementBias: 'upper_back_bias_row',
    equipment: ['cable'],
    cableAttachment: ['rope'],
    isCompound: true,
    isUnilateral: false,
    cablePosition: 'low',
    notes: 'Split rope at top for external rotation'
  },
  {
    id: 'upper_back_landmine_row_bilateral',
    name: 'Bilateral Landmine Row',
    muscleGroup: 'upper_back',
    secondaryMuscles: ['lats', 'biceps'],
    movementBias: 'upper_back_bias_row',
    equipment: ['landmine'],
    isCompound: true,
    isUnilateral: false
  },
  {
    id: 'upper_back_chest_supported_bb_row',
    name: 'Chest Supported Barbell Row',
    muscleGroup: 'upper_back',
    secondaryMuscles: ['lats', 'biceps', 'rear_delt'],
    movementBias: 'upper_back_bias_row',
    equipment: ['barbell', 'bench'],
    isCompound: true,
    isUnilateral: false,
    benchAngle: 'incline'
  },
  {
    id: 'upper_back_chest_supported_db_row',
    name: 'Chest Supported Dumbbell Row',
    muscleGroup: 'upper_back',
    secondaryMuscles: ['lats', 'biceps', 'rear_delt'],
    movementBias: 'upper_back_bias_row',
    equipment: ['dumbbell', 'bench'],
    isCompound: true,
    isUnilateral: false,
    benchAngle: 'incline'
  },
  
  // Shrugs & Pulls
  {
    id: 'upper_back_shrug_db',
    name: 'Dumbbell Shrug',
    muscleGroup: 'upper_back',
    movementBias: 'upper_back_bias_row',
    equipment: ['dumbbell'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Trap emphasis'
  },
  {
    id: 'upper_back_shrug_bb',
    name: 'Barbell Shrug',
    muscleGroup: 'upper_back',
    movementBias: 'upper_back_bias_row',
    equipment: ['barbell'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Trap emphasis'
  },
  {
    id: 'upper_back_kelso_shrug',
    name: 'Kelso Shrug',
    muscleGroup: 'upper_back',
    movementBias: 'upper_back_bias_row',
    equipment: ['dumbbell', 'bench'],
    isCompound: false,
    isUnilateral: false,
    benchAngle: 'incline',
    notes: 'Chest supported, lower trap emphasis'
  },
  {
    id: 'upper_back_deadlift',
    name: 'Deadlift',
    muscleGroup: 'upper_back',
    secondaryMuscles: ['hamstrings', 'glutes', 'lats'],
    movementBias: 'upper_back_bias_row',
    equipment: ['barbell'],
    isCompound: true,
    isUnilateral: false
  },
  {
    id: 'upper_back_rack_pull',
    name: 'Rack Pull',
    muscleGroup: 'upper_back',
    secondaryMuscles: ['hamstrings', 'glutes'],
    movementBias: 'upper_back_bias_row',
    equipment: ['barbell'],
    isCompound: true,
    isUnilateral: false,
    notes: 'Upper back and trap emphasis'
  }
];

// ============================================
// SHOULDER EXERCISES - SIDE DELT
// ============================================

export const SIDE_DELT_EXERCISES: Exercise[] = [
  {
    id: 'side_delt_lateral_raise_db',
    name: 'Dumbbell Lateral Raise',
    muscleGroup: 'side_delt',
    movementBias: 'side_delt_isolation',
    equipment: ['dumbbell'],
    isCompound: false,
    isUnilateral: false
  },
  {
    id: 'side_delt_lateral_raise_cable',
    name: 'Cable Lateral Raise',
    muscleGroup: 'side_delt',
    movementBias: 'side_delt_isolation',
    equipment: ['cable'],
    cableAttachment: ['d_handle'],
    isCompound: false,
    isUnilateral: true,
    cablePosition: 'low'
  },
  {
    id: 'side_delt_upright_row',
    name: 'Upright Row',
    muscleGroup: 'side_delt',
    secondaryMuscles: ['upper_back', 'front_delt'],
    movementBias: 'side_delt_isolation',
    equipment: ['barbell', 'dumbbell', 'cable'],
    isCompound: true,
    isUnilateral: false,
    notes: 'Wide grip for side delt emphasis'
  },
  {
    id: 'side_delt_y_raise',
    name: 'Y Raise',
    muscleGroup: 'side_delt',
    secondaryMuscles: ['rear_delt', 'upper_back'],
    movementBias: 'side_delt_isolation',
    equipment: ['cable', 'dumbbell'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'low',
    notes: 'Arms form Y shape at top'
  }
];

// ============================================
// SHOULDER EXERCISES - REAR DELT
// ============================================

export const REAR_DELT_EXERCISES: Exercise[] = [
  {
    id: 'rear_delt_face_pull',
    name: 'Face Pull',
    muscleGroup: 'rear_delt',
    secondaryMuscles: ['upper_back'],
    movementBias: 'rear_delt_isolation',
    equipment: ['cable'],
    cableAttachment: ['rope'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'high'
  },
  {
    id: 'rear_delt_fly_cable',
    name: 'Cable Rear Delt Fly',
    muscleGroup: 'rear_delt',
    movementBias: 'rear_delt_isolation',
    equipment: ['cable'],
    cableAttachment: ['d_handle'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'mid',
    notes: 'Cross cables in front'
  },
  {
    id: 'rear_delt_fly_db_bent',
    name: 'Bent Over Dumbbell Rear Delt Fly',
    muscleGroup: 'rear_delt',
    movementBias: 'rear_delt_isolation',
    equipment: ['dumbbell'],
    isCompound: false,
    isUnilateral: false
  },
  {
    id: 'rear_delt_band_pull_apart',
    name: 'Band Pull-Apart',
    muscleGroup: 'rear_delt',
    secondaryMuscles: ['upper_back'],
    movementBias: 'rear_delt_isolation',
    equipment: ['bodyweight'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Use resistance band'
  }
];

// ============================================
// SHOULDER EXERCISES - FRONT DELT
// ============================================

export const FRONT_DELT_EXERCISES: Exercise[] = [
  {
    id: 'front_delt_ohp_bb',
    name: 'Barbell Overhead Press',
    muscleGroup: 'front_delt',
    secondaryMuscles: ['triceps', 'side_delt'],
    movementBias: 'front_delt_press',
    equipment: ['barbell'],
    isCompound: true,
    isUnilateral: false
  },
  {
    id: 'front_delt_ohp_smith',
    name: 'Smith Machine Overhead Press',
    muscleGroup: 'front_delt',
    secondaryMuscles: ['triceps', 'side_delt'],
    movementBias: 'front_delt_press',
    equipment: ['smith_machine'],
    isCompound: true,
    isUnilateral: false
  },
  {
    id: 'front_delt_ohp_db',
    name: 'Dumbbell Overhead Press',
    muscleGroup: 'front_delt',
    secondaryMuscles: ['triceps', 'side_delt'],
    movementBias: 'front_delt_press',
    equipment: ['dumbbell'],
    isCompound: true,
    isUnilateral: false
  },
  {
    id: 'front_delt_front_raise',
    name: 'Front Raise',
    muscleGroup: 'front_delt',
    movementBias: 'front_delt_isolation',
    equipment: ['dumbbell', 'cable', 'barbell'],
    isCompound: false,
    isUnilateral: false
  },
  {
    id: 'front_delt_landmine_press',
    name: 'Landmine Press',
    muscleGroup: 'front_delt',
    secondaryMuscles: ['chest', 'triceps'],
    movementBias: 'front_delt_press',
    equipment: ['landmine'],
    isCompound: true,
    isUnilateral: true
  },
  {
    id: 'front_delt_dead_hang',
    name: 'Dead Hang',
    muscleGroup: 'front_delt',
    secondaryMuscles: ['lats'],
    movementBias: 'front_delt_isolation',
    equipment: ['pull_up_bar', 'bodyweight'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Shoulder health and mobility'
  }
];

// ============================================
// BICEP EXERCISES
// ============================================

export const BICEP_EXERCISES: Exercise[] = [
  // Cable Curls
  {
    id: 'biceps_cable_curl',
    name: 'Cable Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_short_head',
    equipment: ['cable'],
    cableAttachment: ['straight_bar', 'ez_curl_bar', 'rope'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'low'
  },
  {
    id: 'biceps_bayesian_curl',
    name: 'Bayesian Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_long_head',
    equipment: ['cable'],
    cableAttachment: ['d_handle'],
    isCompound: false,
    isUnilateral: true,
    cablePosition: 'low',
    notes: 'Cable behind body, arm extended back at start'
  },
  
  // Dumbbell Curls
  {
    id: 'biceps_db_curl',
    name: 'Dumbbell Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_short_head',
    equipment: ['dumbbell'],
    isCompound: false,
    isUnilateral: false
  },
  {
    id: 'biceps_concentration_curl',
    name: 'Concentration Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_short_head',
    equipment: ['dumbbell'],
    isCompound: false,
    isUnilateral: true
  },
  {
    id: 'biceps_incline_curl',
    name: 'Incline Dumbbell Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_long_head',
    equipment: ['dumbbell', 'bench'],
    isCompound: false,
    isUnilateral: false,
    benchAngle: 'incline',
    notes: 'Long head stretch emphasis'
  },
  {
    id: 'biceps_spider_curl',
    name: 'Spider Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_short_head',
    equipment: ['dumbbell', 'bench'],
    isCompound: false,
    isUnilateral: false,
    benchAngle: 'incline',
    notes: 'Chest on incline bench, arms hanging'
  },
  {
    id: 'biceps_preacher_curl',
    name: 'Preacher Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_short_head',
    equipment: ['dumbbell', 'ez_bar', 'cable'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Use incline bench as preacher pad'
  },
  
  // Barbell Curls
  {
    id: 'biceps_bb_curl',
    name: 'Barbell Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_short_head',
    equipment: ['barbell'],
    isCompound: false,
    isUnilateral: false
  },
  {
    id: 'biceps_ez_curl',
    name: 'EZ Bar Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_short_head',
    equipment: ['ez_bar'],
    isCompound: false,
    isUnilateral: false
  },
  {
    id: 'biceps_drag_curl',
    name: 'Drag Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_long_head',
    equipment: ['barbell', 'ez_bar', 'dumbbell'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Elbows travel back, bar drags up torso'
  },
  
  // Hammer/Brachialis
  {
    id: 'biceps_hammer_curl',
    name: 'Hammer Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_brachialis',
    equipment: ['dumbbell'],
    isCompound: false,
    isUnilateral: false,
    grip: 'neutral'
  },
  {
    id: 'biceps_pinwheel_curl',
    name: 'Pinwheel Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_brachialis',
    equipment: ['dumbbell'],
    isCompound: false,
    isUnilateral: true,
    grip: 'neutral',
    notes: 'Cross body hammer curl'
  },
  {
    id: 'biceps_reverse_curl',
    name: 'Reverse Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_brachialis',
    equipment: ['barbell', 'ez_bar', 'cable'],
    isCompound: false,
    isUnilateral: false,
    grip: 'overhand',
    notes: 'Forearm and brachialis emphasis'
  },
  
  // Landmine
  {
    id: 'biceps_landmine_curl',
    name: 'Landmine Curl',
    muscleGroup: 'biceps',
    movementBias: 'bicep_short_head',
    equipment: ['landmine'],
    isCompound: false,
    isUnilateral: false
  },
  
  // Protocols
  {
    id: 'biceps_21s',
    name: '21s',
    muscleGroup: 'biceps',
    movementBias: 'bicep_short_head',
    equipment: ['barbell', 'ez_bar', 'dumbbell', 'cable'],
    isCompound: false,
    isUnilateral: false,
    notes: '7 bottom half, 7 top half, 7 full ROM'
  }
];

// ============================================
// TRICEP EXERCISES
// ============================================

export const TRICEP_EXERCISES: Exercise[] = [
  // Overhead Extensions
  {
    id: 'triceps_overhead_ext_cable',
    name: 'Cable Overhead Tricep Extension',
    muscleGroup: 'triceps',
    movementBias: 'tricep_long_head',
    equipment: ['cable'],
    cableAttachment: ['rope', 'straight_bar', 'ez_curl_bar'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'low'
  },
  {
    id: 'triceps_overhead_ext_rope',
    name: 'Rope Overhead Extension',
    muscleGroup: 'triceps',
    movementBias: 'tricep_long_head',
    equipment: ['cable'],
    cableAttachment: ['rope'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'low',
    notes: 'Split rope at bottom for extra stretch'
  },
  {
    id: 'triceps_overhead_ext_db',
    name: 'Dumbbell Overhead Extension',
    muscleGroup: 'triceps',
    movementBias: 'tricep_long_head',
    equipment: ['dumbbell'],
    isCompound: false,
    isUnilateral: false
  },
  
  // Pushdowns
  {
    id: 'triceps_pressdown_cable',
    name: 'Cable Tricep Pressdown',
    muscleGroup: 'triceps',
    movementBias: 'tricep_lateral_head',
    equipment: ['cable'],
    cableAttachment: ['straight_bar', 'v_bar', 'rope'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'high'
  },
  {
    id: 'triceps_pushdown_cable',
    name: 'Cable Tricep Pushdown',
    muscleGroup: 'triceps',
    movementBias: 'tricep_lateral_head',
    equipment: ['cable'],
    cableAttachment: ['rope', 'v_bar'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'high'
  },
  
  // Pressing Movements
  {
    id: 'triceps_close_grip_bench',
    name: 'Close Grip Bench Press',
    muscleGroup: 'triceps',
    secondaryMuscles: ['chest', 'front_delt'],
    movementBias: 'tricep_medial_head',
    equipment: ['barbell', 'smith_machine', 'bench'],
    isCompound: true,
    isUnilateral: false,
    grip: 'close'
  },
  {
    id: 'triceps_close_grip_pushup',
    name: 'Close Grip Push-up',
    muscleGroup: 'triceps',
    secondaryMuscles: ['chest', 'front_delt'],
    movementBias: 'tricep_medial_head',
    equipment: ['bodyweight'],
    isCompound: true,
    isUnilateral: false,
    grip: 'close'
  },
  {
    id: 'triceps_jm_press',
    name: 'JM Press',
    muscleGroup: 'triceps',
    movementBias: 'tricep_medial_head',
    equipment: ['barbell', 'smith_machine', 'bench'],
    isCompound: true,
    isUnilateral: false,
    notes: 'Hybrid skullcrusher/close grip press'
  },
  
  // Dips
  {
    id: 'triceps_dips',
    name: 'Tricep Dips',
    muscleGroup: 'triceps',
    secondaryMuscles: ['chest', 'front_delt'],
    movementBias: 'tricep_lateral_head',
    equipment: ['dip_bars', 'bodyweight'],
    isCompound: true,
    isUnilateral: false,
    notes: 'Upright torso for tricep emphasis'
  },
  
  // Skullcrushers
  {
    id: 'triceps_skullcrusher',
    name: 'Skullcrusher',
    muscleGroup: 'triceps',
    movementBias: 'tricep_long_head',
    equipment: ['ez_bar', 'barbell', 'dumbbell', 'bench'],
    isCompound: false,
    isUnilateral: false,
    benchAngle: 'flat'
  },
  
  // Kickbacks
  {
    id: 'triceps_kickback_db',
    name: 'Dumbbell Kickback',
    muscleGroup: 'triceps',
    movementBias: 'tricep_lateral_head',
    equipment: ['dumbbell'],
    isCompound: false,
    isUnilateral: true
  },
  {
    id: 'triceps_kickback_cable',
    name: 'Cable Kickback',
    muscleGroup: 'triceps',
    movementBias: 'tricep_lateral_head',
    equipment: ['cable'],
    cableAttachment: ['d_handle'],
    isCompound: false,
    isUnilateral: true,
    cablePosition: 'low'
  }
];

// ============================================
// QUAD EXERCISES
// ============================================

export const QUAD_EXERCISES: Exercise[] = [
  // Squats
  {
    id: 'quads_bb_squat',
    name: 'Barbell Back Squat',
    muscleGroup: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    movementBias: 'quad_compound',
    equipment: ['barbell'],
    isCompound: true,
    isUnilateral: false
  },
  {
    id: 'quads_smith_squat',
    name: 'Smith Machine Squat',
    muscleGroup: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    movementBias: 'quad_compound',
    equipment: ['smith_machine'],
    isCompound: true,
    isUnilateral: false
  },
  {
    id: 'quads_hack_squat_narrow',
    name: 'Hack Squat (Narrow Stance)',
    muscleGroup: 'quads',
    secondaryMuscles: ['glutes'],
    movementBias: 'quad_compound',
    equipment: ['machine'],
    isCompound: true,
    isUnilateral: false,
    stance: 'narrow',
    notes: 'Platz style - feet together, high on platform'
  },
  {
    id: 'quads_hack_squat_wide',
    name: 'Hack Squat (Wide Stance)',
    muscleGroup: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    movementBias: 'quad_compound',
    equipment: ['machine'],
    isCompound: true,
    isUnilateral: false,
    stance: 'wide'
  },
  {
    id: 'quads_sissy_squat',
    name: 'Sissy Squat',
    muscleGroup: 'quads',
    movementBias: 'quad_isolation',
    equipment: ['bodyweight'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Rectus femoris emphasis'
  },
  {
    id: 'quads_landmine_squat',
    name: 'Landmine Squat',
    muscleGroup: 'quads',
    secondaryMuscles: ['glutes'],
    movementBias: 'quad_compound',
    equipment: ['landmine'],
    isCompound: true,
    isUnilateral: false
  },
  
  // Leg Press
  {
    id: 'quads_leg_press_narrow',
    name: 'Leg Press (Narrow Stance)',
    muscleGroup: 'quads',
    secondaryMuscles: ['glutes'],
    movementBias: 'quad_compound',
    equipment: ['machine'],
    isCompound: true,
    isUnilateral: false,
    stance: 'narrow',
    notes: 'Feet low on platform for quad emphasis'
  },
  {
    id: 'quads_leg_press_wide',
    name: 'Leg Press (Wide Stance)',
    muscleGroup: 'quads',
    secondaryMuscles: ['glutes', 'hamstrings'],
    movementBias: 'quad_compound',
    equipment: ['machine'],
    isCompound: true,
    isUnilateral: false,
    stance: 'wide'
  },
  
  // Lunges
  {
    id: 'quads_bulgarian_split_squat',
    name: 'Bulgarian Split Squat',
    muscleGroup: 'quads',
    secondaryMuscles: ['glutes'],
    movementBias: 'quad_compound',
    equipment: ['dumbbell', 'barbell', 'smith_machine', 'bench'],
    isCompound: true,
    isUnilateral: true,
    stance: 'split'
  },
  {
    id: 'quads_smith_split_squat',
    name: 'Smith Machine Split Squat',
    muscleGroup: 'quads',
    secondaryMuscles: ['glutes'],
    movementBias: 'quad_compound',
    equipment: ['smith_machine'],
    isCompound: true,
    isUnilateral: true,
    stance: 'split'
  },
  {
    id: 'quads_reverse_lunge',
    name: 'Reverse Lunge',
    muscleGroup: 'quads',
    secondaryMuscles: ['glutes'],
    movementBias: 'quad_compound',
    equipment: ['dumbbell', 'barbell', 'bodyweight'],
    isCompound: true,
    isUnilateral: true,
    stance: 'split'
  },
  
  // Isolation
  {
    id: 'quads_leg_extension',
    name: 'Leg Extension',
    muscleGroup: 'quads',
    movementBias: 'quad_isolation',
    equipment: ['machine'],
    isCompound: false,
    isUnilateral: false
  }
];

// ============================================
// HAMSTRING EXERCISES
// ============================================

export const HAMSTRING_EXERCISES: Exercise[] = [
  // Curls
  {
    id: 'hamstrings_lying_curl',
    name: 'Lying Leg Curl',
    muscleGroup: 'hamstrings',
    movementBias: 'hamstring_curl',
    equipment: ['machine'],
    isCompound: false,
    isUnilateral: false
  },
  
  // Hinges
  {
    id: 'hamstrings_rdl_bb',
    name: 'Barbell Romanian Deadlift',
    muscleGroup: 'hamstrings',
    secondaryMuscles: ['glutes', 'upper_back'],
    movementBias: 'hamstring_hinge',
    equipment: ['barbell'],
    isCompound: true,
    isUnilateral: false
  },
  {
    id: 'hamstrings_rdl_db',
    name: 'Dumbbell Romanian Deadlift',
    muscleGroup: 'hamstrings',
    secondaryMuscles: ['glutes', 'upper_back'],
    movementBias: 'hamstring_hinge',
    equipment: ['dumbbell'],
    isCompound: true,
    isUnilateral: false
  },
  {
    id: 'hamstrings_stiff_leg_dl',
    name: 'Stiff Leg Deadlift',
    muscleGroup: 'hamstrings',
    secondaryMuscles: ['glutes', 'upper_back'],
    movementBias: 'hamstring_hinge',
    equipment: ['barbell'],
    isCompound: true,
    isUnilateral: false,
    notes: 'Less knee bend than RDL'
  },
  {
    id: 'hamstrings_cable_pull_through',
    name: 'Cable Pull Through',
    muscleGroup: 'hamstrings',
    secondaryMuscles: ['glutes'],
    movementBias: 'hamstring_hinge',
    equipment: ['cable'],
    cableAttachment: ['rope'],
    isCompound: true,
    isUnilateral: false,
    cablePosition: 'low'
  }
];

// ============================================
// GLUTE EXERCISES
// ============================================

export const GLUTE_EXERCISES: Exercise[] = [
  {
    id: 'glutes_hip_thrust_bb',
    name: 'Barbell Hip Thrust',
    muscleGroup: 'glutes',
    secondaryMuscles: ['hamstrings'],
    movementBias: 'glute_compound',
    equipment: ['barbell', 'bench'],
    isCompound: true,
    isUnilateral: false
  },
  {
    id: 'glutes_hip_thrust_db',
    name: 'Dumbbell Hip Thrust',
    muscleGroup: 'glutes',
    secondaryMuscles: ['hamstrings'],
    movementBias: 'glute_compound',
    equipment: ['dumbbell', 'bench'],
    isCompound: true,
    isUnilateral: false
  }
];

// ============================================
// CALF EXERCISES
// ============================================

export const CALF_EXERCISES: Exercise[] = [
  {
    id: 'calves_leg_press_calf_raise',
    name: 'Leg Press Calf Raise',
    muscleGroup: 'calves',
    movementBias: 'calf_gastroc',
    equipment: ['machine'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Straight legs for gastrocnemius'
  },
  {
    id: 'calves_standing_calf_raise',
    name: 'Standing Calf Raise',
    muscleGroup: 'calves',
    movementBias: 'calf_gastroc',
    equipment: ['smith_machine', 'dumbbell', 'bodyweight'],
    isCompound: false,
    isUnilateral: false
  },
  {
    id: 'calves_single_leg_calf_raise',
    name: 'Single Leg Calf Raise',
    muscleGroup: 'calves',
    movementBias: 'calf_gastroc',
    equipment: ['dumbbell', 'bodyweight'],
    isCompound: false,
    isUnilateral: true
  }
];

// ============================================
// ABS/CORE EXERCISES
// ============================================

export const ABS_EXERCISES: Exercise[] = [
  // Flexion
  {
    id: 'abs_lying_leg_raise',
    name: 'Lying Leg Raise',
    muscleGroup: 'abs',
    movementBias: 'abs_flexion',
    equipment: ['bodyweight', 'bench'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Lower abs emphasis'
  },
  {
    id: 'abs_hanging_leg_raise',
    name: 'Hanging Leg Raise',
    muscleGroup: 'abs',
    movementBias: 'abs_flexion',
    equipment: ['pull_up_bar', 'bodyweight'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Lower abs emphasis'
  },
  {
    id: 'abs_cable_crunch',
    name: 'Cable Crunch',
    muscleGroup: 'abs',
    movementBias: 'abs_flexion',
    equipment: ['cable'],
    cableAttachment: ['rope'],
    isCompound: false,
    isUnilateral: false,
    cablePosition: 'high'
  },
  {
    id: 'abs_situp',
    name: 'Sit-up',
    muscleGroup: 'abs',
    movementBias: 'abs_flexion',
    equipment: ['bodyweight'],
    isCompound: false,
    isUnilateral: false
  },
  
  // Rotation
  {
    id: 'abs_russian_twist',
    name: 'Russian Twist',
    muscleGroup: 'abs',
    movementBias: 'abs_rotation',
    equipment: ['bodyweight', 'dumbbell'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Oblique emphasis'
  },
  {
    id: 'abs_landmine_twist',
    name: 'Landmine Twist',
    muscleGroup: 'abs',
    movementBias: 'abs_rotation',
    equipment: ['landmine'],
    isCompound: false,
    isUnilateral: false,
    notes: 'Oblique emphasis'
  },
  
  // Isometric
  {
    id: 'abs_plank',
    name: 'Plank',
    muscleGroup: 'abs',
    movementBias: 'abs_isometric',
    equipment: ['bodyweight'],
    isCompound: false,
    isUnilateral: false
  },
  {
    id: 'abs_side_plank',
    name: 'Side Plank',
    muscleGroup: 'abs',
    movementBias: 'abs_isometric',
    equipment: ['bodyweight'],
    isCompound: false,
    isUnilateral: true,
    notes: 'Oblique emphasis'
  },
  
  // Vacuum
  {
    id: 'abs_vacuum',
    name: 'Stomach Vacuum',
    muscleGroup: 'abs',
    movementBias: 'abs_vacuum',
    equipment: ['bodyweight'],
    isCompound: false,
    isUnilateral: false,
    notes: 'X-Frame daily waist training - TVA activation'
  }
];

// ============================================
// COMBINED EXERCISE LIBRARY
// ============================================

export const EXERCISE_LIBRARY: Exercise[] = [
  ...CHEST_EXERCISES,
  ...LAT_EXERCISES,
  ...UPPER_BACK_EXERCISES,
  ...SIDE_DELT_EXERCISES,
  ...REAR_DELT_EXERCISES,
  ...FRONT_DELT_EXERCISES,
  ...BICEP_EXERCISES,
  ...TRICEP_EXERCISES,
  ...QUAD_EXERCISES,
  ...HAMSTRING_EXERCISES,
  ...GLUTE_EXERCISES,
  ...CALF_EXERCISES,
  ...ABS_EXERCISES
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get all exercises for a specific muscle group
 */
export function getExercisesByMuscleGroup(muscleGroup: MuscleGroup): Exercise[] {
  return EXERCISE_LIBRARY.filter(ex => ex.muscleGroup === muscleGroup);
}

/**
 * Get all exercises matching a movement bias
 */
export function getExercisesByBias(bias: MovementBias): Exercise[] {
  return EXERCISE_LIBRARY.filter(ex => ex.movementBias === bias);
}

/**
 * Get exercises that can be performed with specific equipment
 */
export function getExercisesByEquipment(equipment: Equipment): Exercise[] {
  return EXERCISE_LIBRARY.filter(ex => ex.equipment.includes(equipment));
}

/**
 * Get compound exercises only
 */
export function getCompoundExercises(): Exercise[] {
  return EXERCISE_LIBRARY.filter(ex => ex.isCompound);
}

/**
 * Get isolation exercises only
 */
export function getIsolationExercises(): Exercise[] {
  return EXERCISE_LIBRARY.filter(ex => !ex.isCompound);
}

/**
 * Get unilateral exercises only
 */
export function getUnilateralExercises(): Exercise[] {
  return EXERCISE_LIBRARY.filter(ex => ex.isUnilateral);
}

/**
 * Find alternative exercises for a given exercise
 * Returns exercises with same muscle group and movement bias
 */
export function findAlternatives(exerciseId: string): Exercise[] {
  const exercise = EXERCISE_LIBRARY.find(ex => ex.id === exerciseId);
  if (!exercise) return [];
  
  return EXERCISE_LIBRARY.filter(ex => 
    ex.id !== exerciseId &&
    ex.muscleGroup === exercise.muscleGroup &&
    ex.movementBias === exercise.movementBias
  );
}

/**
 * Find similar exercises (same muscle group, any bias)
 */
export function findSimilar(exerciseId: string): Exercise[] {
  const exercise = EXERCISE_LIBRARY.find(ex => ex.id === exerciseId);
  if (!exercise) return [];
  
  return EXERCISE_LIBRARY.filter(ex => 
    ex.id !== exerciseId &&
    ex.muscleGroup === exercise.muscleGroup
  );
}

/**
 * Search exercises by name
 */
export function searchExercises(query: string): Exercise[] {
  const lowerQuery = query.toLowerCase();
  return EXERCISE_LIBRARY.filter(ex =>
    ex.name.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Find an exercise by name (fuzzy matching)
 * Handles variations in naming conventions
 */
export function findExerciseByName(name: string): Exercise | undefined {
  const lowerName = name.toLowerCase().trim();

  // Try exact match first
  let match = EXERCISE_LIBRARY.find(ex => ex.name.toLowerCase() === lowerName);
  if (match) return match;

  // Try contains match
  match = EXERCISE_LIBRARY.find(ex => ex.name.toLowerCase().includes(lowerName));
  if (match) return match;

  // Try reverse contains (name contains exercise name)
  match = EXERCISE_LIBRARY.find(ex => lowerName.includes(ex.name.toLowerCase()));
  if (match) return match;

  // Try word-based matching (at least 2 words match)
  const nameWords = lowerName.split(/\s+/).filter(w => w.length > 2);
  if (nameWords.length >= 2) {
    match = EXERCISE_LIBRARY.find(ex => {
      const exWords = ex.name.toLowerCase().split(/\s+/);
      const matchCount = nameWords.filter(w => exWords.some(ew => ew.includes(w) || w.includes(ew))).length;
      return matchCount >= 2;
    });
  }

  return match;
}

/**
 * Get smart alternatives for an exercise
 * Returns exercises grouped by relevance:
 * 1. Same movement bias (best alternatives)
 * 2. Same muscle group, different bias (acceptable alternatives)
 */
export function getSmartAlternatives(exerciseName: string, limit = 10): {
  sameBias: Exercise[];
  sameMuscle: Exercise[];
} {
  const exercise = findExerciseByName(exerciseName);

  if (!exercise) {
    // If no match found, return empty arrays
    return { sameBias: [], sameMuscle: [] };
  }

  const sameBias = EXERCISE_LIBRARY.filter(ex =>
    ex.id !== exercise.id &&
    ex.movementBias === exercise.movementBias
  ).slice(0, limit);

  const sameMuscle = EXERCISE_LIBRARY.filter(ex =>
    ex.id !== exercise.id &&
    ex.muscleGroup === exercise.muscleGroup &&
    ex.movementBias !== exercise.movementBias
  ).slice(0, limit);

  return { sameBias, sameMuscle };
}

// ============================================
// EXERCISE COUNT SUMMARY
// ============================================

export const EXERCISE_COUNTS = {
  chest: CHEST_EXERCISES.length,
  lats: LAT_EXERCISES.length,
  upperBack: UPPER_BACK_EXERCISES.length,
  sideDelt: SIDE_DELT_EXERCISES.length,
  rearDelt: REAR_DELT_EXERCISES.length,
  frontDelt: FRONT_DELT_EXERCISES.length,
  biceps: BICEP_EXERCISES.length,
  triceps: TRICEP_EXERCISES.length,
  quads: QUAD_EXERCISES.length,
  hamstrings: HAMSTRING_EXERCISES.length,
  glutes: GLUTE_EXERCISES.length,
  calves: CALF_EXERCISES.length,
  abs: ABS_EXERCISES.length,
  total: EXERCISE_LIBRARY.length
};

// ============================================
// EXPORTS
// ============================================

export default {
  EXERCISE_LIBRARY,
  CHEST_EXERCISES,
  LAT_EXERCISES,
  UPPER_BACK_EXERCISES,
  SIDE_DELT_EXERCISES,
  REAR_DELT_EXERCISES,
  FRONT_DELT_EXERCISES,
  BICEP_EXERCISES,
  TRICEP_EXERCISES,
  QUAD_EXERCISES,
  HAMSTRING_EXERCISES,
  GLUTE_EXERCISES,
  CALF_EXERCISES,
  ABS_EXERCISES,
  EXERCISE_COUNTS,
  getExercisesByMuscleGroup,
  getExercisesByBias,
  getExercisesByEquipment,
  getCompoundExercises,
  getIsolationExercises,
  getUnilateralExercises,
  findAlternatives,
  findSimilar,
  searchExercises,
  findExerciseByName,
  getSmartAlternatives
};
